/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { useState } from "react";
import { Modal, ControlledModal } from "components/Modal";
import { RunDashboardModalContent } from "./RunDashboardModalContent";
import { ParameterApplyModalContent } from "./ParameterApplyModalContent";
import { AllQueriesDeprecatedModalContent } from "./AllQueriesDeprecatedModalContent";
import { AnchorButton } from "components/Button/AnchorButton";
import { useLoginUrl } from "lib/hooks/useLoginUrl";
import { useSession } from "gui/session/session";
import { ActiveContext, useActiveContext } from "shared/ContextSwitcher/store";
import { FullDashboard } from "lib/entries/types";
import { ParameterMerge } from "lib/parameters/types";
import React from "react";
import styles from "./RefreshDashboard.module.css";
import { executeQueries } from "./api/executeQueries";
import { useAnalytics } from "gui/analytics/analytics";
import { useGetRefreshDashboardArguments } from "./useGetRefreshDashboardArguments";
import { Button } from "components/Button/Button";
import { IconPerformance } from "shared/SelectPerformance/SelectPerformance";
import { PerformanceSettingsButton } from "shared/PerformanceSettingsButton/PerformanceSettingsButton";
import { ButtonCombo } from "components/ButtonCombo/ButtonCombo";
import { appliedParametersForQuery } from "lib/parameters/hooks";
import { uniqueBy } from "lib/array";
import type { Parameter } from "lib/parameters/types";

interface Props {
  dashboard: FullDashboard;
  parameters: ParameterMerge;
  apiKey: string | undefined;

  isRunModalOpen: boolean;
  setIsRunModalOpen: (open: boolean) => void;
}

export const RefreshDashboardButton: React.FC<Props> = (props) => {
  const { dashboard, parameters, apiKey } = props;

  const session = useSession();
  const activeContext = useActiveContext();

  const { captureEvent } = useAnalytics();

  const loginUrl = useLoginUrl();

  const {
    queryIds,
    unexecutableQueryIds,
    performance,
    setPerformance,
    selectedPerformanceTier,
    performanceTiers,
  } = useGetRefreshDashboardArguments(
    dashboard.visualization_widgets || [],
    activeContext
  );

  const [paramsThatJustRan, setParamsThatJustRan] = useState<Parameter[]>([]);

  function addParamsThatJustRan(params: Parameter[]) {
    setParamsThatJustRan((paramsThatJustRan) => {
      return paramsThatJustRan.concat(params).filter(uniqueBy(isEqualToParam));
    });
  }

  const queryIdsWithNonDefaultParamsThatDidNotJustRun = queryIds.filter(
    (queryId) => {
      const parametersForQuery = appliedParametersForQuery(queryId, parameters);

      return parametersForQuery.some(
        (param) =>
          param.value !== param.default_value &&
          !paramsThatJustRan.find(isEqualToParam(param))
      );
    }
  );

  const canRefreshDashboard = canShowRefreshDashboardButton(
    activeContext,
    performance
  );

  if (queryIds.length === 0) {
    if (unexecutableQueryIds.length > 0) {
      return (
        <Modal
          size="M"
          label="Apply parameters"
          content={({ close }) => (
            <AllQueriesDeprecatedModalContent close={close} />
          )}
          trigger={({ onClick }) => (
            <Button onClick={onClick} size="M" theme="primary-light">
              Run
            </Button>
          )}
        />
      );
    }

    return null;
  }

  const canShowPerformanceSelector =
    activeContext?.serviceTier.version === "v2" &&
    performance &&
    Boolean(performanceTiers?.length);

  if (!session || !session.user || !activeContext) {
    return (
      <AnchorButton theme="primary-light" size="M" href={loginUrl}>
        Run
      </AnchorButton>
    );
  }

  if (!canRefreshDashboard) {
    return null;
  }

  function runAllQueries() {
    captureEvent("RefreshDashboard: Run Queries clicked");

    props.parameters.state.onApply();

    if (!session || !activeContext) {
      return;
    }

    executeQueries({
      session,
      activeContext,
      performance:
        activeContext.serviceTier.version === "v2" ? performance : undefined,
      queryIds,
      parameters,
      apiKey,
    });

    addParamsThatJustRan(parameters.state.appliedMerged);
  }

  function runQueriesWithNonDefaultParams() {
    if (!session || !activeContext) {
      return;
    }

    executeQueries({
      session,
      activeContext,
      performance:
        activeContext.serviceTier.version === "v2" ? performance : undefined,
      queryIds: queryIdsWithNonDefaultParamsThatDidNotJustRun,
      parameters,
      apiKey,
    });

    addParamsThatJustRan(parameters.state.appliedMerged);

    captureEvent("Dashboard parameter apply modal: Applied interactively");
  }

  const runButton = ({ onClick }: { onClick: () => void }) => (
    <Button onClick={onClick} size="M" theme="primary-light">
      {canShowPerformanceSelector && (
        <IconPerformance performance={performance} />
      )}
      Run
    </Button>
  );

  function getCost(queryCount: number) {
    if (selectedPerformanceTier && canShowPerformanceSelector) {
      return queryCount * selectedPerformanceTier.credits;
    }
    return 0;
  }

  return (
    <ButtonCombo className={styles.refreshButtonGroup}>
      {queryIdsWithNonDefaultParamsThatDidNotJustRun.length > 0 ? (
        <ControlledModal
          size="M"
          label="Apply parameters"
          content={({ close }) => (
            <ParameterApplyModalContent
              activeContext={activeContext}
              getCost={getCost}
              queryIds={queryIds}
              queryIdsWithNonDefaultParamsThatDidNotJustRun={
                queryIdsWithNonDefaultParamsThatDidNotJustRun
              }
              unexecutableQueryCount={unexecutableQueryIds.length}
              runAllQueries={runAllQueries}
              runQueriesWithNonDefaultParams={runQueriesWithNonDefaultParams}
              close={close}
            />
          )}
          onDismiss={() => {
            captureEvent("Dashboard parameter apply modal: Dismissed");
          }}
          trigger={runButton}
          isOpen={props.isRunModalOpen}
          setIsOpen={props.setIsRunModalOpen}
        />
      ) : (
        <ControlledModal
          size="M"
          label="Refresh dashboard"
          content={({ close }) => (
            <RunDashboardModalContent
              activeContext={activeContext}
              cost={getCost(queryIds.length)}
              queryCount={queryIds.length}
              unexecutableQueryCount={unexecutableQueryIds.length}
              runAllQueries={runAllQueries}
              close={close}
            />
          )}
          trigger={runButton}
          isOpen={props.isRunModalOpen}
          setIsOpen={props.setIsRunModalOpen}
        />
      )}

      {canShowPerformanceSelector && (
        <PerformanceSettingsButton
          performance={performance}
          setPerformance={setPerformance}
          activeContext={activeContext}
          performanceTiers={performanceTiers}
          buttonTheme="primary-light"
        />
      )}
    </ButtonCombo>
  );
};

function canShowRefreshDashboardButton(
  activeContext?: ActiveContext,
  performance?: string
): boolean {
  if (
    !activeContext ||
    (activeContext.serviceTier.version === "v2" && !performance) // performance must be provided for the v2 tier
  ) {
    return false;
  }

  return activeContext.serviceTier.hasPlanSubscription;
}

const isEqualToParam = (param: Parameter) => (other: Parameter) =>
  param.key === other.key && param.value === other.value;
